import { useState, useEffect } from "react";
import { Clock, RotateCcw } from "lucide-react";
import BONavigation from "../../../components/admin/BONavigation";
import axios from "axios";
import env from "../../../variables/config";

axios.defaults.baseURL = env.apiUrl;
const config = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

interface QueryLog {
    _id: string;
    endpoint: "database" | "signals";
    params: Record<string, string>;
    hitCount: number;
    lastUsedAt: string;
}

const SKIP_PARAMS = new Set(["is_active", "sortBy", "order", "isActive"]);

function ParamBadges({ params }: { params: Record<string, string> }) {
    const entries = Object.entries(params).filter(([k, v]) => v && !SKIP_PARAMS.has(k));
    if (entries.length === 0) return <span className="text-gray-400 text-xs">Aucun filtre</span>;
    return (
        <div className="flex flex-wrap gap-1">
            {entries.map(([k, v]) => (
                <span key={k} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 border border-gray-200 rounded text-[11px] text-gray-700">
                    <span className="text-gray-400">{k}</span>
                    <span className="font-medium">{String(v).length > 20 ? String(v).slice(0, 20) + "…" : v}</span>
                </span>
            ))}
        </div>
    );
}

function formatDate(dateString: string) {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins}min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

export default function QueryLogs() {
    const [logs, setLogs] = useState<QueryLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [endpoint, setEndpoint] = useState<"all" | "database" | "signals">("all");

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit: "100" });
            if (endpoint !== "all") params.set("endpoint", endpoint);
            const res = await axios.get(`/admin/query-logs?${params}`, config());
            setLogs(res.data.logs || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const clearLogs = async () => {
        if (!window.confirm("Effacer tous les logs de cette vue ?")) return;
        const params = new URLSearchParams();
        if (endpoint !== "all") params.set("endpoint", endpoint);
        await axios.delete(`/admin/query-logs?${params}`, config());
        fetchLogs();
    };

    useEffect(() => { fetchLogs(); }, [endpoint]);

    const totalHits = logs.reduce((s, l) => s + l.hitCount, 0);
    const maxHits = logs[0]?.hitCount || 1;

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-4">
                <div className="mb-6 flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">Query Logs</h1>
                        <p className="text-sm text-gray-500 mt-0.5">Filtres les plus utilisés par votre équipe</p>
                    </div>
                </div>

                <BONavigation />

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-white border border-gray-200 rounded-xl p-4">
                        <p className="text-xs text-gray-500 mb-1">Combinaisons uniques</p>
                        <p className="text-2xl font-semibold text-gray-900">{logs.length}</p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-4">
                        <p className="text-xs text-gray-500 mb-1">Total requêtes</p>
                        <p className="text-2xl font-semibold text-gray-900">{totalHits.toLocaleString()}</p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-4">
                        <p className="text-xs text-gray-500 mb-1">Requête la plus populaire</p>
                        <p className="text-2xl font-semibold text-gray-900">{maxHits.toLocaleString()}x</p>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 flex items-center justify-between">
                    <div className="flex gap-2">
                        {(["all", "database", "signals"] as const).map((e) => (
                            <button
                                key={e}
                                onClick={() => setEndpoint(e)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                    endpoint === e
                                        ? "bg-[#1A1A1A] text-white"
                                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                }`}
                            >
                                {e === "all" ? "Tous" : e === "database" ? "Base" : "Signaux"}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={clearLogs}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-red-600 transition-colors"
                    >
                        <RotateCcw className="w-3.5 h-3.5" />
                        Effacer
                    </button>
                </div>

                {/* Table */}
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    {loading ? (
                        <div className="flex items-center justify-center py-16 text-gray-400 text-sm">Chargement…</div>
                    ) : logs.length === 0 ? (
                        <div className="flex items-center justify-center py-16 text-gray-400 text-sm">Aucune donnée</div>
                    ) : (
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-gray-100">
                                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 w-8">#</th>
                                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 w-24">Endpoint</th>
                                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Filtres</th>
                                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 w-40">Popularité</th>
                                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 w-32">Dernière use</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map((log, i) => (
                                    <tr key={log._id} className="border-b border-gray-50 hover:bg-[#F9F6F3]">
                                        <td className="px-4 py-3 text-xs text-gray-400">{i + 1}</td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${
                                                log.endpoint === "database"
                                                    ? "bg-blue-50 text-blue-700 border border-blue-100"
                                                    : "bg-green-50 text-green-700 border border-green-100"
                                            }`}>
                                                {log.endpoint === "database" ? "Base" : "Signaux"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <ParamBadges params={log.params} />
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full bg-[#1A1A1A] rounded-full"
                                                        style={{ width: `${(log.hitCount / maxHits) * 100}%` }}
                                                    />
                                                </div>
                                                <span className="text-xs font-medium text-gray-900 w-8 text-right">
                                                    {log.hitCount}x
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1 text-xs text-gray-500">
                                                <Clock className="w-3.5 h-3.5" />
                                                {formatDate(log.lastUsedAt)}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}
